import { supabase, isSupabaseConfigured } from "@/lib/supabaseClient";
import { adminService } from "@/services/adminService";

// Helper to clean and sanitize filenames for storage safety
function getSafeFileName(fileName: string): string {
  return fileName
    .normalize("NFD") // split accent characters
    .replace(/[\u0300-\u036f]/g, "") // remove accents
    .replace(/[^a-zA-Z0-9._-]/g, "-") // sanitize to safe characters
    .toLowerCase();
}

// Safety limits
const MAX_IMAGE_SIZE = 5 * 1024 * 1024; // 5MB
const MAX_AUDIO_SIZE = 50 * 1024 * 1024; // 50MB

const ALLOWED_IMAGE_EXTS = [".jpg", ".jpeg", ".png", ".webp"];
const ALLOWED_AUDIO_EXTS = [".mp3", ".wav", ".ogg", ".m4a", ".aac"];

export interface UploadMediaParams {
  file: File;
  questionId?: string;
  importBatchId?: string;
  mediaKind: "audio" | "option-image";
  optionLabel?: "A" | "B" | "C" | "D";
}

/**
 * Validates whether a given media URL is permanent (i.e. http/https and not a temporary local reference).
 */
export function isPermanentMediaUrl(value: string | null | undefined): boolean {
  if (!value) return false;
  const trimmed = value.trim();
  if (trimmed.startsWith("blob:")) return false;
  if (trimmed.startsWith("data:")) return false;
  if (trimmed.startsWith("media_")) return false;
  if (!trimmed.startsWith("http://") && !trimmed.startsWith("https://")) return false;
  return true;
}

export const questionMediaUploadService = {
  /**
   * Centralized function to validate and upload question media files to Supabase Storage,
   * returning their permanent public URL.
   */
  async uploadQuestionMediaFile(params: UploadMediaParams): Promise<string> {
    const { file, questionId, importBatchId, mediaKind, optionLabel } = params;

    if (!isSupabaseConfigured || !supabase) {
      throw new Error("Supabase não está configurado. Não é possível fazer upload.");
    }

    // 1. Get authenticated user
    const { data: userData, error: userError } = await supabase.auth.getUser();
    if (userError || !userData?.user) {
      throw new Error("Usuário não autenticado. Faça login novamente.");
    }

    const userId = userData.user.id;

    // 2. Validate admin permissions
    const isAdmin = await adminService.isCurrentUserAdmin(userData.user);
    if (!isAdmin) {
      throw new Error("Apenas administradores podem enviar mídias de questões.");
    }

    // 3. Determine bucket and perform validation
    const bucket = mediaKind === "audio" ? "question-audios" : "question-images";
    const extension = "." + file.name.split(".").pop()?.toLowerCase();

    if (mediaKind === "option-image") {
      const isValidType = ALLOWED_IMAGE_EXTS.includes(extension) || file.type.startsWith("image/");
      if (!isValidType) {
        throw new Error(`Formato de imagem inválido. Use apenas: ${ALLOWED_IMAGE_EXTS.join(", ")}`);
      }
      if (file.size > MAX_IMAGE_SIZE) {
        throw new Error(`Imagem excede o tamanho máximo de 5MB. Tamanho do arquivo: ${(file.size / (1024 * 1024)).toFixed(2)}MB`);
      }
    } else {
      const allowedMimes = [
        "audio/mpeg", "audio/mp3", "audio/wav", "audio/x-wav", "audio/ogg", 
        "audio/mp4", "audio/x-m4a", "audio/aac", "audio/m4a"
      ];
      let isValidType = false;
      if (file.type) {
        isValidType = allowedMimes.includes(file.type) || file.type.startsWith("audio/");
      } else {
        isValidType = ALLOWED_AUDIO_EXTS.includes(extension);
      }
      if (!isValidType && ALLOWED_AUDIO_EXTS.includes(extension)) {
        isValidType = true;
      }
      if (!isValidType) {
        throw new Error("Formato de áudio não suportado. Use MP3, WAV, OGG, M4A ou AAC.");
      }
      if (file.size > MAX_AUDIO_SIZE) {
        throw new Error(`Áudio excede o tamanho máximo de 50MB. Tamanho do arquivo: ${(file.size / (1024 * 1024)).toFixed(2)}MB`);
      }
    }

    // 4. Generate unique path
    // Format: {userId}/co/{importBatchId}/{questionId}/[audio|option-X]-{timestamp}-{safeFileName}.[ext]
    const batchId = importBatchId || crypto.randomUUID();
    const qId = questionId || crypto.randomUUID();
    const timestamp = Date.now();
    const safeName = getSafeFileName(file.name);
    
    let kindPrefix = "audio";
    if (mediaKind === "option-image") {
      kindPrefix = optionLabel ? `option-${optionLabel.toLowerCase()}` : "option-image";
    }

    const path = `${userId}/co/${batchId}/${qId}/${kindPrefix}-${timestamp}-${safeName}`;

    // 5. Upload to Supabase Storage
    const { error } = await supabase.storage
      .from(bucket)
      .upload(path, file, {
        cacheControl: "3600",
        upsert: false,
        contentType: file.type || undefined
      });

    if (error) {
      console.error(`[COMediaUpload] Supabase Storage upload error`, {
        message: error.message,
        bucket,
        path
      });
      if (error.message.includes("new row violates row-level security policy") || error.message.includes("policy")) {
        throw new Error("Não foi possível enviar o arquivo. Verifique se sua conta admin tem permissão para enviar mídias.");
      }
      throw new Error(`Erro ao enviar arquivo para o Supabase Storage: ${error.message}`);
    }

    // 6. Get and return Public URL
    const { data: publicUrlData } = supabase.storage
      .from(bucket)
      .getPublicUrl(path);

    if (!publicUrlData?.publicUrl) {
      throw new Error("Não foi possível recuperar a URL pública do arquivo enviado.");
    }

    return publicUrlData.publicUrl;
  }
};
